import { useState } from 'react';
import { Checkbox, Modal } from 'react-bootstrap';

import {
  QUICK_IMPORT_SNIPPET_DETECTION,
  QUICK_IMPORT_VENDORED_DETECTION,
} from '../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { BUTTON_TYPE, ButtonGroup } from '../../generic';
import Button from '../../generic/Button';
import Toggle from '../../generic/Toggle';
import Tooltip from '../../generic/Tooltip';

import type { ScanMethodSelections } from './types';

import styles from './ModalQuickImportScanMethods.module.less';

type Props = {
  show: boolean;
  onBack: () => void;
  onConfirm: (selections: ScanMethodSelections) => void;
  defaultVendoredEnabled: boolean;
  defaultSnippetEnabled: boolean;
  canSavePreference?: boolean;
};

const ModalQuickImportScanMethods = (props: Props) => {
  const [isVendoredEnabled, setIsVendoredEnabled] = useState<boolean>(props.defaultVendoredEnabled);
  const [isSnippetEnabled, setIsSnippetEnabled] = useState<boolean>(props.defaultSnippetEnabled);
  const [shouldSavePreference, setShouldSavePreference] = useState<boolean>(false);

  const hasVendoredFF = useIsFeatureEnabled(QUICK_IMPORT_VENDORED_DETECTION);
  const hasSnippetFF = useIsFeatureEnabled(QUICK_IMPORT_SNIPPET_DETECTION);

  const handleConfirm = () =>
    props.onConfirm({
      quickImportVendoredDetectionEnabled: hasVendoredFF && isVendoredEnabled,
      quickImportSnippetDetectionEnabled: hasSnippetFF && isSnippetEnabled,
      quickImportShouldSavePreference: (hasVendoredFF || hasSnippetFF) && shouldSavePreference,
    });

  return (
    <Modal show={props.show} onHide={props.onBack}>
      <Modal.Header>
        <Modal.Title>Scan additional code sources for risk</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className={styles.intro}>
          Include snippets and vendored dependencies to get more complete security and licensing insights.
        </p>
        <Tooltip
          id="quick-import-vendored-tooltip"
          tooltip="Quick Import vendored dependency detection is not enabled for this organization."
          showTooltip={!hasVendoredFF}
          placement="top"
        >
          <div className={styles.toggleRow}>
            <Toggle
              aria-label="Scan for Vendored Dependencies"
              checked={hasVendoredFF && isVendoredEnabled}
              onChange={setIsVendoredEnabled}
              isDisabled={!hasVendoredFF}
            />
            <span className={`${styles.toggleLabel} ${!hasVendoredFF ? styles.toggleLabelDisabled : ''}`}>
              Scan for Vendored Dependencies
            </span>
          </div>
        </Tooltip>
        <Tooltip
          id="quick-import-snippet-tooltip"
          tooltip="Quick Import snippet detection is not enabled for this organization."
          showTooltip={!hasSnippetFF}
          placement="top"
        >
          <div className={styles.toggleRow}>
            <Toggle
              aria-label="Scan for Snippets"
              checked={hasSnippetFF && isSnippetEnabled}
              onChange={setIsSnippetEnabled}
              isDisabled={!hasSnippetFF}
            />
            <span className={`${styles.toggleLabel} ${!hasSnippetFF ? styles.toggleLabelDisabled : ''}`}>
              Scan for Snippets
            </span>
          </div>
        </Tooltip>
        <Tooltip
          id="quick-import-save-preference-tooltip"
          tooltip="You don't have permission to update the organization's default quick import scan settings."
          showTooltip={!props.canSavePreference}
          placement="top"
        >
          <span className={styles.savePreference}>
            <Checkbox
              aria-label="Save this preference for future imports"
              checked={shouldSavePreference}
              onChange={() => setShouldSavePreference((prev) => !prev)}
              disabled={!props.canSavePreference}
            >
              Save this preference for future imports
            </Checkbox>
          </span>
        </Tooltip>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} onClick={props.onBack}>Back</Button>
          <Button type={BUTTON_TYPE.PRIMARY} onClick={handleConfirm}>Import Projects</Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalQuickImportScanMethods;
